import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { CompanySearchField } from '@features/companies/elements/CompanySearchField'
import { isFrenchCompany } from '@features/companies/utils/isFrenchCompany'
import { mapCompanyToCompanyDetails } from '@features/companies/utils/mapCompanyToCompanyDetails'
import { useInvoiceComplianceMode } from '@features/invoices/hooks/useInvoiceComplianceMode'
import { Company, FrenchCompany } from '@modules-deprecated/app/companies/types'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useMissingCompanyDetailsForm } from '../../../../contexts/missingCompanyDetailsFormContext'

export const CompanyName = (): ReactElement => {
  const { t } = useTranslation()
  const { isFrenchComplianceMode } = useInvoiceComplianceMode()
  const { organization } = useUserOrganization()
  const {
    missingCompanyDetailsForm: { FormItem, getValues, reset, setValue },
  } = useMissingCompanyDetailsForm()

  const handleSelectCompany = useCallback(
    (company: Partial<Company> | Partial<FrenchCompany>) => {
      const companyDetails = mapCompanyToCompanyDetails(company, organization?.countryId)

      // Exclude vatNo from the generic spread — it is gated separately by shouldUpdateVatNo.
      // Only apply non-empty values so autofill can't wipe an already-complete
      // (and possibly hidden) field and block the form from being submitted.
      const { vatNo: _, ...companyDetailsWithoutVatNo } = companyDetails
      const autoCompletedCompanyDetails = Object.fromEntries(
        Object.entries(companyDetailsWithoutVatNo).filter(([, value]) => value !== ''),
      )

      const formValues = getValues()
      const currentVatNo = formValues.vatNo
      const isVatNoAutoFilled = formValues.autoCompletedFields?.vatNo
      const isFrench = isFrenchCompany(company)
      const shouldUpdateVatNo = isFrench && !organization?.isVatExempted && (!currentVatNo || isVatNoAutoFilled)

      reset({
        ...formValues,
        ...autoCompletedCompanyDetails,
        ...(isFrenchComplianceMode && { isSiretPendingAssignment: false }),
        ...(shouldUpdateVatNo && { vatNo: companyDetails.vatNo || '' }),
      })

      // Ensure the reset has been fully applied before setting `autoCompletedFields`, so the highlight state is not wiped out.
      window.requestAnimationFrame(() => {
        setValue('autoCompletedFields', {
          companyAddress: !!companyDetails.companyAddress,
          companyCity: !!companyDetails.companyCity,
          companyZipCode: !!companyDetails.companyZipCode,
          registrationNo: !!companyDetails.registrationNo,
          ...(isFrenchComplianceMode && {
            legalStatusCustom: !!companyDetails.legalStatusCustom,
            legalStatusOption: !!companyDetails.legalStatusOption,
          }),
          ...(shouldUpdateVatNo && { vatNo: !!companyDetails.vatNo }),
        })
      })
    },
    [getValues, isFrenchComplianceMode, organization?.countryId, organization?.isVatExempted, reset, setValue],
  )

  return (
    <FormItem
      label={t('external_invoices.missing_company_compliance_details.modal.form.field.company_name.label')}
      name="companyName"
      render={({ field }) => {
        return <CompanySearchField {...field} onSelectCompany={handleSelectCompany} size="sm" />
      }}
    />
  )
}
